/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { Fragment, useEffect, useState } from 'react';
import {
  Spin,
  Button,
  Form,
  message,
  Input,
  Select,
  Col,
  Row,
  Card,
  Tag,
  Space,
  Empty,
  Typography,
  Modal,
} from 'antd';
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  GlobalOutlined,
  ApartmentOutlined,
  InfoCircleOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, queryCache } from 'react-query';
import {
  createProjectContact,
  deleteProjectContact,
  getProjectContactsList,
  patchProjectContact,
} from '~src/services/api';
import { ContactInformationVM } from '~src/types/db.d.ts';
import { useIntl } from 'react-intl';

const { Option } = Select;
const { Text } = Typography;

const typeItems = [
  { labelId: 'id-word-contact-type-personal-card', key: '1000' },
  { labelId: 'id-word-contact-type-business-card', key: '2000' },
];

export const ContactSettingsTab = ({
  active,
  projectIdOverride,
}: {
  active: boolean;
  projectIdOverride?: string;
}) => {
  const intl = useIntl();
  const projectId = projectIdOverride;

  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  const [mode, setMode] = useState<'view' | 'create' | 'edit'>('view');
  const [selectedType, setSelectedType] = useState<string>();
  const [
    editingContact,
    setEditingContact,
  ] = useState<ContactInformationVM | null>(null);
  const [filterType, setFilterType] = useState<'all' | '1000' | '2000'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const invalidateAllContacts = () => {
    queryCache.invalidateQueries(['contacts', projectId]);
    queryCache.invalidateQueries(['projectContacts']);
  };

  const { data: contacts, isLoading } = useQuery(
    ['contacts', projectId],
    () => getProjectContactsList(null, { projectId }),
    { enabled: !!projectId }
  );

  const [doCreateContact, { isLoading: isCreating }] = useMutation(
    (data) => createProjectContact(projectId as any, data),
    {
      onSuccess: () => {
        message.success(
          intl.formatMessage({ id: 'id-word-contact-created-success' })
        );
        invalidateAllContacts();
        setMode('view');
        form.resetFields();
      },
      onError: () =>
        message.error(
          intl.formatMessage({ id: 'id-word-contact-created-error' })
        ),
    }
  );

  const [doPatchContact, { isLoading: isPatching }] = useMutation(
    ({ contact_key, contactData }: any) =>
      patchProjectContact(projectId as any, contact_key, contactData),
    {
      onSuccess: () => {
        message.success(
          intl.formatMessage({ id: 'id-word-contact-updated-success' })
        );
        invalidateAllContacts();
        setMode('view');
        setEditingContact(null);
      },
      onError: () =>
        message.error(
          intl.formatMessage({ id: 'id-word-contact-updated-error' })
        ),
    }
  );

  const [doDeleteContact] = useMutation(
    (contactKey: string) => deleteProjectContact(projectId as any, contactKey),
    {
      onSuccess: () => {
        message.success(
          intl.formatMessage({ id: 'id-word-contact-deleted-success' })
        );
        invalidateAllContacts();
        setMode('view');
      },
      onError: () =>
        message.error(
          intl.formatMessage({ id: 'id-word-contact-deleted-error' })
        ),
    }
  );

  const resetState = () => {
    form.resetFields();
    editForm.resetFields();
    setMode('view');
    setSelectedType(undefined);
    setEditingContact(null);
    setFilterType('all');
  };

  // When the tab becomes active (or modal opened), reset like ResourceSettings modal did
  useEffect(() => {
    if (active) resetState();
  }, [active]);

  const handleSubmit = async (values: any) => {
    await doCreateContact(values);
  };

  const handleEditSubmit = async (values: any) => {
    if (!editingContact) return;
    await doPatchContact({
      contact_key: (editingContact as any).contact_key,
      contactData: values,
    });
  };

  const showDeleteConfirm = (contactKey: string, contactName: string) => {
    Modal.confirm({
      title: intl.formatMessage({ id: 'id-word-contact-delete-title' }),
      content: intl.formatMessage(
        { id: 'id-word-contact-delete-content' },
        { name: contactName }
      ),
      okText: intl.formatMessage({ id: 'id-word-delete' }),
      okType: 'danger',
      cancelText: intl.formatMessage({ id: 'id-word-cancel' }),
      onOk: () => doDeleteContact(contactKey),
    });
  };

  const filteredContacts = contacts
    ?.filter((c: any) => (filterType === 'all' ? true : c.type === filterType))
    .filter((c: any) => {
      if (!searchTerm) return true;
      const lowerCaseSearch = searchTerm.toLowerCase().trim();

      let searchableFields: any[] = [];

      // Check the contact type and build the search array accordingly
      if (c.type === '1000') {
        // --- Fields for PERSONAL contacts ---
        searchableFields = [
          c.title,
          c.name_1,
          c.name_2,
          c.user_title_1,
          c.user_title_2,
          c.email_1,
          c.email_2,
          c.phone_1,
          c.phone_2,
          c.address_1,
          c.address_2,
          c.location_1,
          c.location_2,
        ];
      } else {
        // --- Fields for BUSINESS contacts ---
        searchableFields = [
          // Business-specific fields
          c.title,
          c.company_title,
          c.company_subtitle,
          c.company_email,
          c.company_url,
          c.postal_code,
          c.city,
          c.country,
          // Common fields
          c.address_1,
          c.address_2,
          c.location_1,
          c.location_2,
          // Personal fields for the contact person at the business
          c.name_1,
          c.name_2,
          c.user_title_1,
          c.user_title_2,
          c.email_1,
          c.email_2,
          c.phone_1,
          c.phone_2,
        ];
      }

      return searchableFields.some(
        (field) =>
          field && field.toString().toLowerCase().includes(lowerCaseSearch)
      );
    });

  if (!projectId) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={intl.formatMessage({ id: 'id-word-no-project-id' })}
      />
    );
  }

  return (
    <Fragment>
      {isLoading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '500px',
          }}>
          <Spin size="large" />
        </div>
      ) : (
        <Row gutter={24}>
          <Col
            span={10}
            css={{
              maxHeight: '60vh',
              overflowY: 'auto',
              paddingRight: '12px',
              paddingTop: '5px',
            }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 12,
              }}>
              <b style={{ fontSize: '16px' }}>
                {intl.formatMessage({ id: 'id-word-contacts' })}
              </b>
              <Button
                type="primary"
                onClick={() => {
                  setMode('create');
                  form.resetFields();
                }}>
                {intl.formatMessage({ id: 'id-word-add-new' })}
              </Button>
            </div>

            <Row gutter={8} style={{ marginBottom: 16 }}>
              <Col span={16}>
                <Input
                  prefix={<SearchOutlined />}
                  placeholder={intl.formatMessage({ id: 'id-word-search' })}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  allowClear
                />
              </Col>
              <Col span={8}>
                <Select
                  value={filterType}
                  onChange={setFilterType}
                  style={{ width: '100%' }}>
                  <Option value="all">
                    {intl.formatMessage({ id: 'id-word-show-all' })}
                  </Option>
                  <Option value="1000">
                    {intl.formatMessage({ id: 'id-word-personal-only' })}
                  </Option>
                  <Option value="2000">
                    {intl.formatMessage({ id: 'id-word-business-only' })}
                  </Option>
                </Select>
              </Col>
            </Row>

            {filteredContacts && filteredContacts.length > 0 ? (
              <Space direction="vertical" style={{ width: '100%' }}>
                {filteredContacts.map((item: ContactInformationVM) => (
                  <Card
                    key={(item as any).contact_key}
                    size="small"
                    actions={[
                      <Button
                        type="text"
                        onClick={() => {
                          // *** THE FIX IS HERE ***
                          // 1. First, reset the form completely to clear any stale values.
                          editForm.resetFields();
                          // 2. Then, set the new contact and its values.
                          setEditingContact(item);
                          editForm.setFieldsValue(item as any);
                          setMode('edit');
                        }}>
                        {intl.formatMessage({ id: 'id-word-edit' })}
                      </Button>,
                      <Button
                        type="text"
                        danger
                        onClick={() =>
                          showDeleteConfirm(
                            (item as any).contact_key,
                            (item as any).name_1 || (item as any).company_title
                          )
                        }>
                        {intl.formatMessage({ id: 'id-word-delete' })}
                      </Button>,
                    ]}>
                    <Card.Meta
                      title={
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                          }}>
                          {(item as any).type === '1000' ? (
                            <UserOutlined />
                          ) : (
                            <ApartmentOutlined />
                          )}
                          <Text strong>
                            {(item as any).title ||
                              (item as any).name_1 ||
                              (item as any).company_title}
                          </Text>
                        </div>
                      }
                      description={
                        (item as any).type === '1000' ? (
                          <Tag color="blue">
                            {intl.formatMessage({
                              id: 'id-word-contact-tag-personal',
                            })}
                          </Tag>
                        ) : (
                          <Tag color="green">
                            {intl.formatMessage({
                              id: 'id-word-contact-tag-business',
                            })}
                          </Tag>
                        )
                      }
                    />
                    <div style={{ marginTop: 12, paddingLeft: 4 }}>
                      <Space direction="vertical" size="small">
                        {(item as any).type === '1000' ? (
                          <Fragment>
                            {(item as any).user_title_1 && (
                              <Text>
                                <InfoCircleOutlined />{' '}
                                {(item as any).user_title_1}
                                {(item as any).user_title_2 &&
                                  ` / ${(item as any).user_title_2}`}
                              </Text>
                            )}
                            {(item as any).email_1 && (
                              <Text>
                                <MailOutlined /> {(item as any).email_1}
                                {(item as any).email_2 &&
                                  ` / ${(item as any).email_2}`}
                              </Text>
                            )}
                            {(item as any).phone_1 && (
                              <Text>
                                <PhoneOutlined /> {(item as any).phone_1}
                                {(item as any).phone_2 &&
                                  ` / ${(item as any).phone_2}`}
                              </Text>
                            )}
                            {(item as any).address_1 && (
                              <Text>
                                <EnvironmentOutlined />{' '}
                                {(item as any).address_1}
                                {(item as any).address_2 &&
                                  `, ${(item as any).address_2}`}
                              </Text>
                            )}
                          </Fragment>
                        ) : (
                          <Fragment>
                            {(item as any).company_email && (
                              <Text>
                                <MailOutlined /> {(item as any).company_email}
                              </Text>
                            )}
                            {(item as any).company_url && (
                              <Text>
                                <GlobalOutlined /> {(item as any).company_url}
                              </Text>
                            )}
                            {(item as any).address_1 && (
                              <Text>
                                <EnvironmentOutlined />{' '}
                                {(item as any).address_1},{' '}
                                {(item as any).postal_code} {(item as any).city}
                              </Text>
                            )}
                            {(item as any).country && (
                              <Text>
                                <GlobalOutlined /> {(item as any).country} (
                                {(item as any).timezone})
                              </Text>
                            )}
                          </Fragment>
                        )}
                      </Space>
                    </div>
                  </Card>
                ))}
              </Space>
            ) : (
              <Empty
                description={intl.formatMessage({
                  id: 'id-word-no-contacts-found',
                })}
                style={{ marginTop: '48px' }}>
                <Button type="primary" onClick={() => setMode('create')}>
                  {intl.formatMessage({ id: 'id-word-create-first-contact' })}
                </Button>
              </Empty>
            )}
          </Col>

          <Col
            span={14}
            css={{
              maxHeight: '60vh',
              overflowY: 'auto',
              paddingRight: '12px',
            }}>
            {mode === 'view' && (
              <div style={{ textAlign: 'center', paddingTop: '80px' }}>
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <span>
                      {intl.formatMessage({
                        id: 'id-word-select-contact-to-edit-or-create',
                      })}
                    </span>
                  }
                />
              </div>
            )}

            {mode === 'create' && (
              <div>
                <b style={{ fontSize: '16px' }}>
                  {intl.formatMessage({ id: 'id-word-create-new-contact' })}
                </b>
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleSubmit}
                  onValuesChange={(changedValues) => {
                    if ('type' in changedValues)
                      setSelectedType((changedValues as any).type);
                  }}>
                  <Form.Item
                    name="type"
                    label={intl.formatMessage({ id: 'id-word-type' })}
                    rules={[{ required: true }]}>
                    <Select
                      placeholder={intl.formatMessage({
                        id: 'id-word-select-contact-type',
                      })}
                      allowClear
                      onChange={(value) => setSelectedType(value as any)}>
                      {typeItems.map((type) => (
                        <Option key={type.key} value={type.key}>
                          {intl.formatMessage({ id: type.labelId })}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  {selectedType === '1000' && (
                    <Fragment>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="name_1"
                            label={intl.formatMessage({ id: 'id-word-name' })}
                            rules={[{ required: true }]}>
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="name_2"
                            label={intl.formatMessage({
                              id: 'id-word-name-2',
                            })}>
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="user_title_1"
                            label={intl.formatMessage({
                              id: 'id-word-title-1',
                            })}>
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="user_title_2"
                            label={intl.formatMessage({
                              id: 'id-word-title-2',
                            })}>
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="email_1"
                            label={intl.formatMessage({ id: 'id-word-email' })}
                            rules={[{ required: true }]}>
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="email_2"
                            label={intl.formatMessage({
                              id: 'id-word-email-2',
                            })}>
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="phone_1"
                            label={intl.formatMessage({ id: 'id-word-phone' })}
                            rules={[{ required: true }]}>
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="phone_2"
                            label={intl.formatMessage({
                              id: 'id-word-phone-2',
                            })}>
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="address_1"
                            label={intl.formatMessage({
                              id: 'id-word-address-1',
                            })}>
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="address_2"
                            label={intl.formatMessage({
                              id: 'id-word-address-2',
                            })}>
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Fragment>
                  )}

                  {selectedType === '2000' && (
                    <Fragment>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="company_title"
                            label={intl.formatMessage({
                              id: 'id-word-company-title',
                            })}
                            rules={[{ required: true }]}>
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="company_subtitle"
                            label={intl.formatMessage({
                              id: 'id-word-company-subtitle',
                            })}>
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="company_email"
                            label={intl.formatMessage({
                              id: 'id-word-company-email',
                            })}
                            rules={[{ required: true }]}>
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="company_url"
                            label={intl.formatMessage({
                              id: 'id-word-company-url',
                            })}>
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="location_1"
                            label={intl.formatMessage({
                              id: 'id-word-location-1',
                            })}>
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="location_2"
                            label={intl.formatMessage({
                              id: 'id-word-location-2',
                            })}>
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="address_1"
                            label={intl.formatMessage({
                              id: 'id-word-address-1',
                            })}>
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="address_2"
                            label={intl.formatMessage({
                              id: 'id-word-address-2',
                            })}>
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="postal_code"
                            label={intl.formatMessage({
                              id: 'id-word-postal-code',
                            })}>
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="city"
                            label={intl.formatMessage({ id: 'id-word-city' })}>
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="region_1"
                            label={intl.formatMessage({
                              id: 'id-word-region-1',
                            })}>
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="region_2"
                            label={intl.formatMessage({
                              id: 'id-word-region-2',
                            })}>
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="country"
                            label={intl.formatMessage({
                              id: 'id-word-country',
                            })}>
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="timezone"
                            label={intl.formatMessage({
                              id: 'id-word-timezone',
                            })}>
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Fragment>
                  )}

                  {selectedType && (
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={isCreating}>
                      {intl.formatMessage({ id: 'id-word-create-contact' })}
                    </Button>
                  )}
                </Form>
              </div>
            )}

            {mode === 'edit' && editingContact && (
              <div>
                <b style={{ fontSize: '16px' }}>
                  {intl.formatMessage(
                    { id: 'id-word-edit-contact-title' },
                    {
                      name:
                        (editingContact as any).name_1 ||
                        (editingContact as any).company_title,
                    }
                  )}
                </b>
                <Form
                  form={editForm}
                  layout="vertical"
                  onFinish={handleEditSubmit}>
                  <Form.Item
                    name="type"
                    label={intl.formatMessage({ id: 'id-word-type' })}>
                    <Select disabled>
                      <Option value="1000">
                        {intl.formatMessage({
                          id: 'id-word-contact-tag-personal',
                        })}
                      </Option>
                      <Option value="2000">
                        {intl.formatMessage({
                          id: 'id-word-contact-tag-business',
                        })}
                      </Option>
                    </Select>
                  </Form.Item>

                  {(editingContact as any).type === '1000' && (
                    <Fragment>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="name_1"
                            label={intl.formatMessage({ id: 'id-word-name' })}>
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="name_2"
                            label={intl.formatMessage({
                              id: 'id-word-name-2',
                            })}>
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="user_title_1"
                            label={intl.formatMessage({
                              id: 'id-word-title-1',
                            })}>
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="user_title_2"
                            label={intl.formatMessage({
                              id: 'id-word-title-2',
                            })}>
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="email_1"
                            label={intl.formatMessage({ id: 'id-word-email' })}>
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="email_2"
                            label={intl.formatMessage({
                              id: 'id-word-email-2',
                            })}>
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="phone_1"
                            label={intl.formatMessage({ id: 'id-word-phone' })}>
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="phone_2"
                            label={intl.formatMessage({
                              id: 'id-word-phone-2',
                            })}>
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="address_1"
                            label={intl.formatMessage({
                              id: 'id-word-address-1',
                            })}>
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="address_2"
                            label={intl.formatMessage({
                              id: 'id-word-address-2',
                            })}>
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="location_1"
                            label={intl.formatMessage({
                              id: 'id-word-location-1',
                            })}>
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="location_2"
                            label={intl.formatMessage({
                              id: 'id-word-location-2',
                            })}>
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Fragment>
                  )}

                  {(editingContact as any).type === '2000' && (
                    <Fragment>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="company_title"
                            label={intl.formatMessage({
                              id: 'id-word-company-title',
                            })}>
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="company_subtitle"
                            label={intl.formatMessage({
                              id: 'id-word-company-subtitle',
                            })}>
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="company_email"
                            label={intl.formatMessage({
                              id: 'id-word-company-email',
                            })}>
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="company_url"
                            label={intl.formatMessage({
                              id: 'id-word-company-url',
                            })}>
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="location_1"
                            label={intl.formatMessage({
                              id: 'id-word-location-1',
                            })}>
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="location_2"
                            label={intl.formatMessage({
                              id: 'id-word-location-2',
                            })}>
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="address_1"
                            label={intl.formatMessage({
                              id: 'id-word-address-1',
                            })}>
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="address_2"
                            label={intl.formatMessage({
                              id: 'id-word-address-2',
                            })}>
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="postal_code"
                            label={intl.formatMessage({
                              id: 'id-word-postal-code',
                            })}>
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="city"
                            label={intl.formatMessage({ id: 'id-word-city' })}>
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="region_1"
                            label={intl.formatMessage({
                              id: 'id-word-region-1',
                            })}>
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="region_2"
                            label={intl.formatMessage({
                              id: 'id-word-region-2',
                            })}>
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="country"
                            label={intl.formatMessage({
                              id: 'id-word-country',
                            })}>
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="timezone"
                            label={intl.formatMessage({
                              id: 'id-word-timezone',
                            })}>
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Fragment>
                  )}

                  <Space>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={isPatching}>
                      {intl.formatMessage({ id: 'id-word-save-changes' })}
                    </Button>
                    <Button onClick={() => setMode('view')}>
                      {intl.formatMessage({ id: 'id-word-cancel' })}
                    </Button>
                  </Space>
                </Form>
              </div>
            )}
          </Col>
        </Row>
      )}
    </Fragment>
  );
};
